<template>
  <content-modal
    ref="createNewNotebookModal"
    class="create-new-notebook-modal"
    :size="size"
    :auto-hide-off="autoHideOff"
    :visible="visible"
    @modal-hide-request="actionHidden"
  >
    <template #title>{{ params?.title }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <cv-form @submit.prevent>
            <dl class="field-set description">
              <dd class="field-value">{{ t("notebook_create_new_notebook_subtitle") }}</dd>
            </dl>
            <dl class="field-set page-title">
              <dt class="field-label">{{ t("notebook_create_new_notebook_input_label") }}</dt>
              <dd class="field-input">
                <text-input
                  :character-limit="NOTEBOOK_EDITOR_VALIDATION.TITLE_CHARACTER_MAX_LENGTH"
                  :mandatory="false"
                  :placeholder="DEFAULT_CREATE_NEW_NOTEBOOK_PLACEHOLDER"
                  :value="createNewNotebookName"
                  @input="handleCreateNewNotebookNameInput"
                />
              </dd>
            </dl>
          </cv-form>
        </template>
        <template #tools>
          <cv-button class="cancel" kind="tertiary" size="field" @click="actionHidden">{{
            t("button_cancel")
          }}</cv-button>
          <fetch-data-button
            class="save"
            v-bind="dataSubmitButton"
            @click="handleSave"
            @reset-error-state="handleSubmitButtonResetError"
          >
            <span>
              {{ t("button_create") }}
            </span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import DOMUtils from "@utils/dom.utils";
import { getCurrentInstance } from "vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import modalEventBus from "@plugins/modal/service/event-bus.ts";
import TextInput from "@plugins/common/components/TextInput.vue";
import {
  NOTEBOOK_EDITOR_VALIDATION,
  DEFAULT_CREATE_NEW_NOTEBOOK_PLACEHOLDER,
  DEFAULT_CREATE_NEW_NOTEBOOK_NAME,
} from "@modules/notebook-editor/notebook-editor-helper.ts";
import { NotebookRecentModel } from "@modules/notebook-editor/notebook-editor-types.ts";
import { createNotebook } from "@modules/notebook/notebook-api.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";

// Props
interface Props {
  params?: {
    title?: string;
  };
}
defineProps<Props>();

defineOptions({
  name: "NotebookCreateNewNotebookModal",
});

// Emits
const emit = defineEmits<{
  createdNotebook: [notebook: NotebookRecentModel];
  cancel: [];
}>();

// Instance (for accessing $modal)
const proxy = getCurrentInstance()?.proxy;
if (!proxy) {
  throw new Error("Proxy is not available");
}

const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();

// Refs and State
const size = ref("small");
const autoHideOff = ref(true);
const visible = ref(true);
const createNewNotebookName = ref("");

const dataSubmitButton = ref({
  kind: "primary",
  completed: false,
  loading: false,
  error: false,
});

// Methods
const handleCreateNewNotebookNameInput = (value: string) => {
  createNewNotebookName.value = value;
};

const resolveNewNotebookName = (name: string) => {
  if (name === "") {
    return DEFAULT_CREATE_NEW_NOTEBOOK_NAME;
  }
  return name;
};

const handleSave = async () => {
  if (!validate()) {
    dataSubmitButton.value.error = true;
    return;
  }
  try {
    const response = await createNotebook({
      title: resolveNewNotebookName(createNewNotebookName.value),
    });
    emit("createdNotebook", response.data);
    hide();
  } catch (e) {
    dataSubmitButton.value.error = true;
    passiveApiFailHandler(e);
  }
};

const validate = () => {
  return (
    createNewNotebookName.value.length <= NOTEBOOK_EDITOR_VALIDATION.TITLE_CHARACTER_MAX_LENGTH
  );
};

interface ModalHideEvent {
  "cv:reason"?: string;
}

const actionHidden = (event: ModalHideEvent) => {
  if (event["cv:reason"] !== "external-click") {
    hide();
  }
  emit("cancel");
};

const hide = () => {
  DOMUtils.removeClass("body", "bx--body--with-modal-open");
  modalEventBus.$emit("modal-hidden", proxy?.$.vnode.key);
};

const handleSubmitButtonResetError = () => {
  dataSubmitButton.value.error = false;
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.create-new-notebook-modal {
  .bx--modal-content {
    overflow-y: visible;
  }
  .bx--form {
    margin-top: 0;
  }

  .description {
    .field-value {
      margin-top: 0;
    }
  }

  :is(.bx--dropdown-link, .dropdown-option) {
    @include carbon--type-style("productive-heading-01");
    position: relative;
    display: flex;
    align-items: center;

    .sls-icon {
      margin-right: $spacing-02;
    }
  }
  .bx--dropdown {
    .bx--list-box__label {
      display: block;
      width: 100%;
    }
  }
}
</style>
